import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminProtectedRoute from './components/common/AdminProtectedRoute';
import Layout from './components/layout/Layout';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import SubscriberList from './pages/admin/SubscriberList';
import SubscriberDetail from './pages/admin/SubscriberDetail';
import SaasInvoiceList from './pages/admin/SaasInvoiceList';
import AdminMetrics from './pages/admin/AdminMetrics';
import Ops from './pages/admin/Ops';
import AiModels from './pages/admin/AiModels';
import Dashboard from './pages/Dashboard';
import Analytique from './pages/analytique/Analytique';
import DeclarationEsg from './pages/analytique/DeclarationEsg';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ClientList from './pages/clients/ClientList';
import ClientForm from './pages/clients/ClientForm';
import CrmPipeline from './pages/crm/CrmPipeline';
import Alertes from './pages/alertes/Alertes';
import Impayes from './pages/recouvrement/Impayes';
import RelancesConfig from './pages/recouvrement/RelancesConfig';
import QualiteDonnees from './pages/qualite/QualiteDonnees';
import ControleFiscal from './pages/fiscalite/ControleFiscal';
import Validations from './pages/workflow/Validations';
import GouvernanceCarbone from './pages/carbone/GouvernanceCarbone';
import PortailClient from './pages/portail/PortailClient';
import PortailFournisseur from './pages/portail/PortailFournisseur';
import Tresorerie from './pages/tresorerie/Tresorerie';
import CockpitDirigeant from './pages/dashboard/CockpitDirigeant';
import CockpitCarbone from './pages/carbone/CockpitCarbone';
import AssistantDevis from './pages/assistant/AssistantDevis';
import ClientWorkspace from './pages/clients/ClientWorkspace';
import FournisseurList from './pages/fournisseurs/FournisseurList';
import FournisseurForm from './pages/fournisseurs/FournisseurForm';
import FournisseurWorkspace from './pages/fournisseurs/FournisseurWorkspace';
import Rapprochement3VoiesPage from './pages/achats/Rapprochement3Voies';
import DepotsFactures from './pages/achats/DepotsFactures';
import ArticleList from './pages/articles/ArticleList';
import ArticleForm from './pages/articles/ArticleForm';
import Produit360 from './pages/articles/Produit360';
import DocumentList from './pages/documents/DocumentList';
import DocumentForm from './pages/documents/DocumentForm';
import DocumentDetail from './pages/documents/DocumentDetail';
import PaiementList from './pages/paiements/PaiementList';
import PaiementForm from './pages/paiements/PaiementForm';
import RapportTVA from './pages/rapports/RapportTVA';
import RapportMarge from './pages/rapports/RapportMarge';
import EtatAnnuel from './pages/rapports/EtatAnnuel';
import ExportComptable from './pages/rapports/ExportComptable';
import Comptabilite from './pages/comptabilite/Comptabilite';
import PlanComptable from './pages/comptabilite/PlanComptable';
import Journaux from './pages/comptabilite/Journaux';
import Ecritures from './pages/comptabilite/Ecritures';
import Balance from './pages/comptabilite/Balance';
import GrandLivre from './pages/comptabilite/GrandLivre';
import MappageComptable from './pages/comptabilite/Mappage';
import Bilan from './pages/comptabilite/Bilan';
import EtatResultat from './pages/comptabilite/EtatResultat';
import Exercices from './pages/comptabilite/Exercices';
import Lettrage from './pages/comptabilite/Lettrage';
import Immobilisations from './pages/comptabilite/Immobilisations';
import RapprochementBancaire from './pages/comptabilite/RapprochementBancaire';
import RapprochementAssiste from './pages/comptabilite/RapprochementAssiste';
import Charges from './pages/comptabilite/Charges';
import ComptaAnalytique from './pages/comptabilite/Analytique';
import ExpertComptable from './pages/comptabilite/ExpertComptable';
import Rh from './pages/rh/Rh';
import Employes from './pages/rh/Employes';
import Conges from './pages/rh/Conges';
import Paie from './pages/rh/Paie';
import StockMovements from './pages/stock/StockMovements';
import DepotsLots from './pages/stock/DepotsLots';
import StockValorisation from './pages/stock/StockValorisation';
import SupplierInvoiceForm from './pages/stock/SupplierInvoiceForm';
import Reappro from './pages/stock/Reappro';
import StockAnalytics from './pages/stock/StockAnalytics';
import AlertesStock from './pages/stock/AlertesStock';
import ParametresLayout from './pages/parametres/ParametresLayout';
import CompanySettings from './pages/parametres/CompanySettings';
import InvoiceTemplates from './pages/parametres/InvoiceTemplates';
import InvoiceTemplateForm from './pages/parametres/InvoiceTemplateForm';
import Numbering from './pages/parametres/Numbering';
import Plan from './pages/parametres/Plan';
import TwoFactorAuth from './pages/parametres/TwoFactorAuth';
import Signature from './pages/parametres/Signature';
import ApiKeys from './pages/parametres/ApiKeys';
import Webhooks from './pages/parametres/Webhooks';
import AuditLog from './pages/parametres/AuditLog';
import Gdpr from './pages/parametres/Gdpr';

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { borderRadius: '12px', background: '#333', color: '#fff', fontSize: '14px' },
              success: { style: { background: '#16a34a' } },
              error: { style: { background: '#dc2626' } },
            }}
          />
          <Routes>
            <Route path="/portail/:token" element={<PortailClient />} />
            <Route path="/portail-fournisseur/:token" element={<PortailFournisseur />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/admin"
              element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}
            >
              <Route index element={<AdminDashboard />} />
              <Route path="subscribers" element={<SubscriberList />} />
              <Route path="subscribers/:id" element={<SubscriberDetail />} />
              <Route path="invoices" element={<SaasInvoiceList />} />
              <Route path="metrics" element={<AdminMetrics />} />
              <Route path="ai-models" element={<AiModels />} />
              <Route path="ops" element={<Ops />} />
            </Route>
            <Route
              path="/"
              element={<ProtectedRoute><RoleGate><Layout /></RoleGate></ProtectedRoute>}
            >
              <Route index element={<Dashboard />} />
              <Route path="analytique" element={<Analytique />} />
              <Route path="analytique/esg" element={<DeclarationEsg />} />
              <Route path="clients" element={<ClientList />} />
              <Route path="crm/pipeline" element={<CrmPipeline />} />
              <Route path="alertes" element={<Alertes />} />
              <Route path="recouvrement/impayes" element={<Impayes />} />
              <Route path="recouvrement/relances" element={<RelancesConfig />} />
              <Route path="qualite-donnees" element={<QualiteDonnees />} />
              <Route path="fiscalite/controle" element={<ControleFiscal />} />
              <Route path="validations" element={<Validations />} />
              <Route path="tresorerie" element={<Tresorerie />} />
              <Route path="dashboard/dirigeant" element={<CockpitDirigeant />} />
              <Route path="carbone/cockpit" element={<CockpitCarbone />} />
              <Route path="carbone/gouvernance" element={<GouvernanceCarbone />} />
              <Route path="assistant/devis" element={<AssistantDevis />} />
              <Route path="clients/new" element={<ClientForm />} />
              <Route path="clients/:id/edit" element={<ClientForm />} />
              <Route path="clients/:id" element={<ClientWorkspace />} />
              <Route path="fournisseurs" element={<FournisseurList />} />
              <Route path="fournisseurs/new" element={<FournisseurForm />} />
              <Route path="fournisseurs/:id/edit" element={<FournisseurForm />} />
              <Route path="fournisseurs/:id/rapprochement" element={<Rapprochement3VoiesPage />} />
              <Route path="fournisseurs/:id" element={<FournisseurWorkspace />} />
              <Route path="articles" element={<ArticleList />} />
              <Route path="articles/new" element={<ArticleForm />} />
              <Route path="articles/:id/edit" element={<ArticleForm />} />
              <Route path="articles/:id/360" element={<Produit360 />} />
              <Route path="documents/new" element={<DocumentForm />} />
              <Route path="documents/view/:id" element={<DocumentDetail />} />
              <Route path="documents/:id/edit" element={<DocumentForm />} />
              <Route path="documents/:type" element={<DocumentList />} />
              <Route path="paiements" element={<PaiementList />} />
              <Route path="paiements/new" element={<PaiementForm />} />
              <Route path="stock/mouvements" element={<StockMovements />} />
              <Route path="stock/depots" element={<DepotsLots />} />
              <Route path="stock/analytique" element={<StockAnalytics />} />
              <Route path="stock/alertes" element={<AlertesStock />} />
              <Route path="stock/valorisation" element={<StockValorisation />} />
              <Route path="stock/reappro" element={<Reappro />} />
              <Route path="achats/depots-factures" element={<DepotsFactures />} />
              <Route path="stock/achats/new" element={<SupplierInvoiceForm />} />
              <Route path="stock/achats/:id/edit" element={<SupplierInvoiceForm />} />
              <Route path="rapports/tva" element={<RapportTVA />} />
              <Route path="rapports/marge" element={<RapportMarge />} />
              <Route path="rapports/etat-annuel" element={<EtatAnnuel />} />
              <Route path="rapports/export" element={<ExportComptable />} />
              <Route path="comptabilite" element={<Comptabilite />} />
              <Route path="comptabilite/journal" element={<Ecritures />} />
              <Route path="comptabilite/grand-livre" element={<GrandLivre />} />
              <Route path="comptabilite/balance" element={<Balance />} />
              <Route path="comptabilite/analytique" element={<ComptaAnalytique />} />
              <Route path="comptabilite/expert" element={<ExpertComptable />} />
              <Route path="comptabilite/bilan" element={<Bilan />} />
              <Route path="comptabilite/etat-resultat" element={<EtatResultat />} />
              <Route path="comptabilite/plan-comptable" element={<PlanComptable />} />
              <Route path="comptabilite/journaux" element={<Journaux />} />
              <Route path="comptabilite/exercices" element={<Exercices />} />
              <Route path="comptabilite/lettrage" element={<Lettrage />} />
              <Route path="comptabilite/immobilisations" element={<Immobilisations />} />
              <Route path="comptabilite/rapprochement" element={<RapprochementBancaire />} />
              <Route path="comptabilite/rapprochement-assiste" element={<RapprochementAssiste />} />
              <Route path="comptabilite/charges" element={<Charges />} />
              <Route path="comptabilite/parametres" element={<MappageComptable />} />
              <Route path="rh" element={<Rh />} />
              <Route path="rh/employes" element={<Employes />} />
              <Route path="rh/conges" element={<Conges />} />
              <Route path="rh/paie" element={<Paie />} />
              <Route path="parametres" element={<ParametresLayout />}>
                <Route index element={<CompanySettings />} />
                <Route path="modeles" element={<InvoiceTemplates />} />
                <Route path="modeles/new" element={<InvoiceTemplateForm />} />
                <Route path="modeles/:id/edit" element={<InvoiceTemplateForm />} />
                <Route path="numerotation" element={<Numbering />} />
                <Route path="plan" element={<Plan />} />
                <Route path="security" element={<TwoFactorAuth />} />
                <Route path="signature" element={<Signature />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="gdpr" element={<Gdpr />} />
              </Route>
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

const RoleGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'PLATFORM_ADMIN') {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

export default App;
